import { Context } from './abstractions/context';
import { MapperResolver } from './mapper-resolver';
import { Resolver } from './types/resolver';
import { ResolverObject } from './types/resolver-object';

export class Mapper {
  private static context: Context = {
    cache: {},
  } as Context;

  public static map<TSource, TDestiny>(
    source: TSource,
    destiny: { new (): TDestiny },
    resolver: Resolver<TSource, TDestiny>[]
  ): TDestiny {
    const _destiny: TDestiny = Reflect.construct(destiny, []);

    if (resolver && resolver.length > 0) {
      resolver.forEach((key: Resolver<TDestiny, TSource>) => {
        this.build(source, _destiny, key);
      });
    }

    return _destiny;
  }

  public static mapByList<TDestiny, TSource>(
    sourceList: TSource[],
    destiny: { new (): TDestiny },
    resolver: Resolver<TSource, TDestiny>[]
  ): TDestiny[] {
    return new MapperResolver(sourceList, destiny, resolver).resolve(
      this.context
    );
  }

  private static build<TSource, TDestiny>(
    source: TSource,
    destiny: TDestiny,
    resolver: Resolver<TSource, TDestiny>
  ): void {
    let value;

    if (typeof resolver === 'object') {
      const props: string[] = Object.keys(resolver);

      props.forEach((prop: string) => {
        const propertiesResolver = resolver as ResolverObject<
          TSource,
          TDestiny
        >;

        const propResolver =
          propertiesResolver[prop as keyof ResolverObject<TSource, TDestiny>];

        if (typeof propResolver === 'function') {
          value = propResolver(
            source,
            destiny as TDestiny[keyof TDestiny],
            this.context
          );
        } else {
          value = propResolver;
        }

        this.setProp(destiny, prop, value as JSONValue);
      });
    } else if (typeof resolver === 'string') {
      let prop = '';
      if (resolver.includes('.')) {
        const props = resolver.split('.');
        value = this.resolveConcatProps(props, source);
        prop = props[props.length - 1];

        this.setProp(destiny, prop, value);
      } else {
        value = source[resolver as keyof TSource];
        this.setProp(destiny, resolver, value as JSONValue);
      }
    }
  }

  private static setProp<TDestiny>(
    destiny: TDestiny,
    prop: string,
    value: JSONValue
  ): void {
    Object.defineProperty(destiny as object, prop, {
      value,
      configurable: true,
      writable: true,
      enumerable: true,
    });
  }

  private static resolveConcatProps<U>(props: string[], source: U): JSONValue {
    let current = source as JSONValue;

    props.forEach((prop: string) => {
      current = current[prop as keyof JSONValue];
    });

    return current;
  }
}
